"use client";
import { useState } from "react";
import View from "../atoms/view";
import Text from "../atoms/text";
import Input from "../atoms/input";

/**
 * @name Switch
 * @description Renders a toggle switch component.
 *
 * @param {Object} props - Component props.
 * @param {boolean} [props.isChecked=false] - The initial checked state of the switch.
 * @param {string} props.label - The label and ID associated with the switch input.
 * @returns {JSX.Element} The rendered switch component.
 */

export interface SwitchProps {
  isChecked?: boolean;
  label: string;
  onChange?: (checked: boolean) => void;
}
const Switch = ({ isChecked = false, label, onChange }: SwitchProps) => {
  const [internalChecked, setInternalChecked] = useState(isChecked);

  // Sync internal state if prop changes (for semi-controlled usage)
  const currentChecked = onChange ? isChecked : internalChecked;

  const handleChange = () => {
    const newValue = !currentChecked;
    if (!onChange) {
      setInternalChecked(newValue);
    } else {
      onChange(newValue);
    }
  };

  return (
    <View className="flex items-center justify-center">
      <Text
        type="label"
        htmlFor={label}
        className="flex items-center cursor-pointer"
      >
        <View className="relative">
          <Input
            name={label}
            type="checkbox"
            id={label}
            onChange={handleChange}
            checked={currentChecked}
            className="sr-only"
          />
          <View
            className={`block ${
              currentChecked ? "bg-primary" : "bg-primary/20"
            } w-14 h-8 rounded-full`}
          ></View>
          <View
            className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition ${
              currentChecked && "translate-x-full"
            }`}
          ></View>
        </View>
      </Text>
    </View>
  );
};

export default Switch;
